import { useSession } from "next-auth/react";
import {
  getIconFromSlug,
  isToolVisible,
  metadata,
  type AITool,
} from "@app/_metadata";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { FileUploadStatusPopover } from "@shared-components/file-upload-status-popover";
import { AboutContent } from "@shared-components/global/about-content";
import { DarkModeSwitch } from "@shared-components/global/dark-mode-switch";
import { ReportBugForm } from "@shared-components/global/report-bug-form";
import { Button } from "@shared-components/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@shared-components/shadcn/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared-components/shadcn/dropdown-menu";
import { api } from "@shared-server/api";
import { compilePath } from "@shared-utils/routing";
import { cn } from "@shared-utils/ui";
import { env } from "@shared/env.mjs";
import {
  AreaChart,
  BookOpenText,
  Bug,
  FileCode,
  FileQuestion,
  GraduationCap,
  Menu,
  Settings,
} from "lucide-react";
import Link from "next/link";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
  useState,
} from "react";

interface TopBarMenuProps {
  activeAiTool: AITool;
}

interface TopBarMenuButtonProps extends ComponentPropsWithoutRef<
  typeof Button
> {
  mobileIcon?: ReactNode;
}

export const TopBarMenuButton = forwardRef<
  HTMLButtonElement,
  TopBarMenuButtonProps
>(({ children, className, mobileIcon, ...props }, ref) => (
  <Button
    ref={ref}
    className={cn(
      "group relative px-1.5 md:px-2 no-underline hover:no-underline hover:opacity-100",
      className,
    )}
    variant="text"
    {...props}
  >
    {mobileIcon ? (
      <span className="md:hidden" aria-hidden="true">
        {mobileIcon}
      </span>
    ) : null}
    <span
      className={cn("relative font-heading font-medium", {
        "hidden md:inline": !!mobileIcon,
      })}
    >
      {children}
      <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-sec-bright-green transition-all duration-300 ease-out group-hover:w-full" />
    </span>
  </Button>
));

TopBarMenuButton.displayName = "TopBarMenuButton";

export const TopBarMenu = (props: TopBarMenuProps) => {
  // Check if user is admin
  const { data: session } = useSession();
  const isAdmin = session?.user.isAdmin ?? false;

  const [showAbout, setShowAbout] = useState(false);
  const [showReportBug, setShowReportBug] = useState(false);

  // Feature flags for user
  const activeFeatureFlags = api.feature.allEnabledForMe.useQuery();

  // Fetch runtime envs to compile sentry link
  let monitorUrl = "";
  if (env.NEXT_PUBLIC_ENV) {
    monitorUrl = `https://leo-pharma.sentry.io/projects/hub-${env.NEXT_PUBLIC_ENV}/?project=4509213606805584`;
  }

  return (
    <>
      <FileUploadStatusPopover />
      <TopBarMenuButton
        aria-label="Bug report"
        mobileIcon={<Bug size={18} strokeWidth={1.75} />}
        onClick={() => setShowReportBug(true)}
      >
        {"Bug report"}
      </TopBarMenuButton>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <TopBarMenuButton
            aria-label="Menu"
            mobileIcon={<Menu size={18} strokeWidth={1.75} />}
          >
            {"Menu"}
          </TopBarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-56"
          side="bottom"
          align="end"
          sideOffset={15}
        >
          <DropdownMenuLabel className="text-sm font-bold">
            {"Tools"}
          </DropdownMenuLabel>
          {metadata.aiTools
            ?.filter((aiTool) => aiTool.tool)
            .filter((appConfig) =>
              isToolVisible(appConfig, activeFeatureFlags.data, isAdmin),
            )
            .map((aiTool) => (
              <DropdownMenuItem asChild key={aiTool.slug}>
                <Link href={compilePath(aiTool.slug)}>
                  <div
                    className={cn(
                      aiTool.meta.strokeColor,
                      "flex h-6 w-6 items-center justify-center rounded-sm",
                    )}
                  >
                    {getIconFromSlug(aiTool.slug, {
                      strokeWidth: 1.5,
                      size: 20,
                      className: aiTool.meta.strokeColor,
                    })}
                  </div>
                  <span className="ml-2 font-heading text-lg">
                    {aiTool.meta.name}
                  </span>
                </Link>
              </DropdownMenuItem>
            ))}
          {activeFeatureFlags.data?.includes("HTML_VIEWER__APP__222723") && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-sm font-bold">
                {"Other Tools"}
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <a href="/html-viewer">
                  <FileCode className="mr-2" strokeWidth={1.5} size={16} />
                  {"HTML Viewer"}
                </a>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-sm font-bold">
            {"Learn"}
          </DropdownMenuLabel>
          {Object.values(metadata.helpLinks).map((link) => (
            <DropdownMenuItem asChild key={link.label}>
              <a target="_blank" rel="noreferrer" href={link.href}>
                {link.key === "academy" ? (
                  <GraduationCap className="mr-2" strokeWidth={1.5} size={16} />
                ) : (
                  <FileQuestion className="mr-2" strokeWidth={1.5} size={16} />
                )}
                {link.label}
              </a>
            </DropdownMenuItem>
          ))}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-sm font-bold">
                {"Admin"}
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <a href="/docs">
                  <BookOpenText className="mr-2" strokeWidth={1.5} size={16} />
                  {"Docs"}
                </a>
              </DropdownMenuItem>
              {monitorUrl && (
                <DropdownMenuItem asChild>
                  <a href={monitorUrl}>
                    <AreaChart className="mr-2" strokeWidth={1.5} size={16} />
                    {"Sentry Monitoring"}
                  </a>
                </DropdownMenuItem>
              )}

              <DropdownMenuItem asChild>
                <a href="/admin/feature-flags">
                  <Settings className="mr-2" strokeWidth={1.5} size={16} />
                  {"Admin"}
                </a>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowAbout(true)}>
            {"About"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DarkModeSwitch />
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={showAbout} onOpenChange={(open) => setShowAbout(open)}>
        <DialogContent className="p-0">
          <VisuallyHidden.Root>
            <DialogTitle>About</DialogTitle>
          </VisuallyHidden.Root>
          <AboutContent
            onClose={() => setShowAbout(false)}
            aiTool={props.activeAiTool}
          />
        </DialogContent>
      </Dialog>
      <ReportBugForm setOpen={setShowReportBug} open={showReportBug} />
    </>
  );
};
